<template>
    <div
        id="product-edit-overview"
        class="product-overview"
    >
        <pre v-if="isDebug">{{ filters }}</pre>
        <div class="productoverviewline">
            <div class="productoverviewcolumn">
                {{ trans('pages.product_edit_overview.sorting') }}
            </div>
            <div class="productoverviewwidecolumn">
                <ui5-select
                    @change="
                        filters.sortField = $event.detail.selectedOption.value
                    "
                >
                    <ui5-option
                        v-for="option in sortFieldOptions"
                        :key="'sortFieldOption.' + option.value"
                        :selected="option.value == filters.sortField"
                        :value="option.value"
                    >
                        {{ option.label }}
                    </ui5-option>
                </ui5-select>
            </div>

            <div class="productoverviewwidecolumn">
                <ui5-select
                    @change="
                        filters.sortOrder = $event.detail.selectedOption.value
                    "
                >
                    <ui5-option
                        v-for="option in sortOrderOptions"
                        :key="'sortOrderOption.' + option.value"
                        :selected="option.value == filters.sortOrder"
                        :value="option.value"
                    >
                        {{ option.label }}
                    </ui5-option>
                </ui5-select>
            </div>

            <div class="productoverviewwidecolumn">
                <ui5-button @click="loadWithReset">
                    {{ trans('pages.product_edit_overview.sort') }}
                </ui5-button>
            </div>

            <div class="productoverviewwidecolumn">
                <ui5-select
                    @change="
                        filters.specialFilter =
                            $event.detail.selectedOption.value
                    "
                >
                    <ui5-option
                        v-for="option in specialFilterOptions"
                        :key="'specialFilterOption.' + option.value"
                        :selected="option.value == filters.specialFilter"
                        :value="option.value"
                    >
                        {{ option.label }}
                    </ui5-option>
                </ui5-select>
            </div>
        </div>

        <div class="productoverviewline">
            <div class="iconcolumn">
                <ui5-checkbox
                    :checked="filters.finished == 1"
                    @change="
                        filters.finished = $event.target.checked ? '1' : '0'
                    "
                />
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-input
                    typ="Text"
                    @input="filters.productnr = $event.target.value"
                    :value="filters.productnr"
                    :placeholder="
                        trans('pages.product_edit_overview.product_nr')
                    "
                    @keyup.enter="loadWithReset"
                />
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-input
                    @input="filters.ordernr = $event.target.value"
                    :value="filters.ordernr"
                    :placeholder="trans('pages.product_edit_overview.order_nr')"
                    @keyup.enter="loadWithReset"
                />
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-input
                    @input="filters.customer = $event.target.value"
                    :value="filters.customer"
                    :placeholder="trans('pages.product_edit_overview.customer')"
                    @keyup.enter="loadWithReset"
                />
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-input
                    @input="filters.reference = $event.target.value"
                    :value="filters.reference"
                    :placeholder="
                        trans('pages.product_edit_overview.reference')
                    "
                    @keyup.enter="loadWithReset"
                />
            </div>
            <div class="productoverviewcolumnmedium auto-complete-column">
                <auto-complete-input
                    v-if="productTypeOptions"
                    v-model="filters.producttype"
                    :initial-search="getProductTypeName(filters.producttype)"
                    :options="productTypeOptions"
                    :placeholder="
                        trans('pages.product_edit_overview.product_type')
                    "
                />
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-input
                    @input="filters.ralnr = $event.target.value"
                    :value="filters.ralnr"
                    :placeholder="trans('pages.product_edit_overview.ral_nr')"
                    @keyup.enter="loadWithReset"
                />
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-input
                    @input="filters.raltype = $event.target.value"
                    :value="filters.raltype"
                    :placeholder="trans('pages.product_edit_overview.ral_type')"
                    @keyup.enter="loadWithReset"
                />
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-input
                    @input="filters.status = $event.target.value"
                    :value="filters.status"
                    :placeholder="trans('pages.product_edit_overview.status')"
                    keyup.enter="loadWithReset"
                />
            </div>
            <div class="productoverviewcolumnmedium auto-complete-column">
                <div>
                    <auto-complete-input
                        v-if="supplierOptions"
                        v-model="filters.supplierId"
                        :initial-search="getSupplierName(filters)"
                        :options="supplierOptions"
                        :placeholder="
                            trans('pages.product_edit_overview.supplier')
                        "
                    />
                </div>
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-date-picker
                    class="simplified"
                    @click="$event.target.openPicker()"
                    :value="filters.deliveryDateIN"
                    @change="filters.deliveryDateIN = $event.detail.value"
                    @keyup.enter="loadWithReset"
                    :placeholder="
                        trans('pages.product_edit_overview.delivery_in')
                    "
                    format-pattern="dd-MM-yyyy"
                />
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-date-picker
                    class="simplified"
                    @click="$event.target.openPicker()"
                    :value="filters.deliveryDate"
                    @change="filters.deliveryDate = $event.detail.value"
                    @keyup.enter="loadWithReset"
                    :placeholder="
                        trans('pages.product_edit_overview.delivery_out')
                    "
                    format-pattern="dd-MM-yyyy"
                />
            </div>
            <div>
                <ui5-date-picker
                    :placeholder="
                        trans('pages.product_edit_overview.production_date')
                    "
                    format-pattern="yyyy-MM-dd"
                    :value="filters.productionDate"
                    @change="
                        filters.productionDate = $event.target.value;
                        loadWithReset;
                    "
                />
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-label>
                    {{ trans('pages.product_edit_overview.price') }}
                </ui5-label>
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-input
                    @input="filters.deliveryDateId = $event.target.value"
                    :value="filters.deliveryDateId"
                    :placeholder="trans('pages.product_edit_overview.ride_nr')"
                    @keyup.enter="loadWithReset"
                />
            </div>
            <div class="productoverviewcolumnmedium">
                <ui5-input
                    @input="filters.deliveryMethod = $event.target.value"
                    :value="filters.deliveryMethod"
                    :placeholder="
                        trans('pages.product_edit_overview.delivery_method')
                    "
                    @keyup.enter="loadWithReset"
                />
            </div>
            <div class="productoverviewgoto">
                <i
                    @click="loadWithReset"
                    class="fa fa-filter fa-2x"
                />
            </div>
            <div class="productoverviewgoto">
                <i
                    @click="clearProductEditOverview"
                    class="fa fa-ban fa-2x"
                />
            </div>
        </div>
        <div id="producteditoverviewlist">
            <div
                v-for="(product, productIndex) in products"
                class="productoverviewline"
                :style="{ backgroundColor: getColor(product) }"
                :id="'product' + product.productId"
            >
                <div class="iconcolumn">
                    <i
                        class="fa fa-info-circle back"
                        @click="goToProductPage(product.productId)"
                    />
                    <i
                        v-if="product.deliveryDateId"
                        class="fa fa-truck back"
                        @click="
                            $router.push(
                                '/deliverydatedetails/' + product.deliveryDateId
                            )
                        "
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput1"
                        :value="product.productNr"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput2"
                        :value="product.orderId"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput4"
                        :value="product.customerName"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput5"
                        :value="product.reference"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput6"
                        :value="product.producttypeName"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput7"
                        :value="product.color ? product.color.ralnr : ''"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput8"
                        :value="product.color ? product.color.raltype : ''"
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput9"
                        :value="product.productstatus || ''"
                        readonly
                    />
                </div>
                <div
                    class="productoverviewcolumnmedium auto-complete-column update-column"
                >
                    <auto-complete-input
                        v-if="supplierEditOptions"
                        :value="product.supplierId"
                        @input="
                            updateProductProperty(product, 'supplierId', $event)
                        "
                        :initial-search="getSupplierName(product)"
                        :options="supplierEditOptions"
                        :placeholder="
                            trans('pages.product_edit_overview.supplier')
                        "
                    />
                </div>
                <div class="productoverviewcolumnmedium update-column">
                    <ui5-date-picker
                        class="simplified"
                        @click="$event.target.openPicker()"
                        :value="product.deliveryDateIN"
                        @change="
                            updateProductProperty(
                                product,
                                'deliveryDateIN',
                                $event.detail.value
                            )
                        "
                        :placeholder="
                            trans('pages.product_edit_overview.delivery_in')
                        "
                        format-pattern="dd-MM-yyyy"
                    />
                </div>
                <div class="productoverviewcolumnmedium update-column">
                    <ui5-date-picker
                        class="simplified"
                        @click="$event.target.openPicker()"
                        :value="product.deliveryDateDay"
                        @change="
                            updateProductProperty(
                                product,
                                'deliveryDateDay',
                                $event.detail.value
                            )
                        "
                        :placeholder="
                            trans('pages.product_edit_overview.delivery_out')
                        "
                        format-pattern="dd-MM-yyyy"
                    />
                </div>
                <div class="productoverviewcolumnmedium update-column">
                    <div>
                        <ui5-date-picker
                            :id="
                                'producteditoverviewproductiondate' +
                                product.productId
                            "
                            format-pattern="yyyy-MM-dd"
                            :value="product.productionDate"
                            @change="
                                updateProductionDateForProduct(product, $event)
                            "
                            :readonly="
                                !hasRight('administration') ||
                                product.productionDateSaving
                            "
                        />
                    </div>
                    <img
                        src="images/ajax-loader.gif"
                        v-if="product.productionDateSaving"
                    />
                    <img
                        src="images/error.png"
                        v-if="product.productionDateError"
                    />
                </div>
                <div class="productoverviewcolumnmedium update-column">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput11"
                        :id="'producteditoverviewprice' + product.productId"
                        @blur="updatePriceForProduct(product, $event)"
                        :readonly="
                            !hasRight('administration') || product.priceSaving
                        "
                        :value="product.price"
                    />
                    <img
                        src="images/ajax-loader.gif"
                        v-if="product.priceSaving"
                    />
                    <img
                        src="images/error.png"
                        v-if="product.priceError"
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput17"
                        :value="
                            product.deliveryDateKey
                                ? product.deliveryDateKey
                                : ''
                        "
                        readonly
                    />
                </div>
                <div class="productoverviewcolumnmedium">
                    <ui5-input
                        type="Text"
                        class="productoverviewinput18"
                        :value="
                            product.deliveryMethod
                                ? product.deliveryMethod.name
                                : ''
                        "
                        readonly
                    />
                </div>
            </div>
        </div>
        <spinner v-if="loading" />
        <div class="productoverviewline">
            {{
                trans('pages.product_edit_overview.total_products', {
                    count: products.length,
                })
            }}
        </div>
    </div>
</template>
<script>
import AutoCompleteInput from '../components/AutoCompleteInput';
import orderApi from '../api/order';
import productApi from '../api/product';
import supplierApi from '../api/supplier';
import Spinner from '../components/loader/Spinner';
import IsDebugMixin from '../mixins/is-debug';
import UI5StylingMixin from '../mixins/ui5-styling';
import {
    clearedFilters,
    sortFieldOptions,
    sortOrderOptions,
    specialFilterOptions,
} from '../shared/productEditOverview';
import NavigationFunctionsMixin from '../mixins/navigation-functions';
import { mapGetters } from 'vuex';
import { isNumber } from '../helpers/GeneralHelper';
import { colorArray } from '../shared/colors';
import { showErrorMessage, showInfoMessage } from '../dialogs';
import {
    fixedSupplierOptions,
    fixedSupplierEditOptions,
    generateSupplierOptions,
} from '../shared/products';
import { getLabel } from '../shared/collections';
import localization from '../mixins/localization';

export default {
    mixins: [
        IsDebugMixin,
        NavigationFunctionsMixin,
        UI5StylingMixin,
        localization,
    ],
    components: { AutoCompleteInput, Spinner },
    computed: {
        ...mapGetters({
            hasRight: 'currentUser/hasRight',
        }),
        sortFieldOptions: () => sortFieldOptions,
        sortOrderOptions: () => sortOrderOptions,
        specialFilterOptions: () => specialFilterOptions,
        supplierOptions() {
            return generateSupplierOptions(
                this.suppliers,
                fixedSupplierOptions
            );
        },
        supplierEditOptions() {
            return generateSupplierOptions(
                this.suppliers,
                fixedSupplierEditOptions
            );
        },
        productTypeOptions() {
            const options = [
                {
                    value: '0',
                    label: this.trans(
                        'pages.product_edit_overview.all_product_types'
                    ),
                },
            ];
            if (this.productTypes) {
                this.productTypes.forEach((productType) => {
                    options.push({
                        value: productType.producttypeId,
                        label: productType.label,
                    });
                });
            }
            return options;
        },
    },
    data() {
        return {
            page: 1,
            loading: false,
            productTypes: null,
            filters: { ...clearedFilters },
            products: [],
            suppliers: null,
        };
    },
    mounted() {
        this.$bus.$on('bottom-scroll', this.loadMore);

        this.filters = {
            ...this.$store.getters['productEditOverview/getFilters'],
        };

        supplierApi.fetchAllSuppliers().then((response) => {
            this.suppliers = response.data.data;
        });

        orderApi.productTypesForOrder().then((response) => {
            this.productTypes = response.data.data;
            this.loadWithReset();
        });
    },
    beforeRouteLeave(to, from, next) {
        this.$bus.$off('bottom-scroll', this.loadMore);
        next();
    },
    methods: {
        getProductTypeName(id) {
            return getLabel(id, this.productTypeOptions);
        },
        updateProductProperty(product, prop, value) {
            if (product[prop] != value) {
                const oldValue = product[prop];
                productApi
                    .updateProductProperty(product.productId, prop, value)
                    .then(() => {
                        showInfoMessage(
                            this.trans(
                                'pages.product_edit_overview.saved_success'
                            )
                        );
                    })
                    .catch(() => {
                        showErrorMessage(
                            this.trans(
                                'pages.product_edit_overview.save_failed'
                            )
                        );
                        product[prop] = oldValue;
                    });
                product[prop] = value;
            }
        },
        getSupplierName(product) {
            const supplierId =
                product && product.supplierId ? product.supplierId : null;
            return getLabel(supplierId, this.supplierOptions);
        },
        loadMore() {
            if (this.loading) {
                return;
            }
            this.page++;
            this.load();
        },
        getColor(product) {
            const colorIndex = Math.max(Math.min(product.daysPastDue, 9), 0);
            return colorArray[colorIndex];
        },
        load() {
            const payload = {
                page: this.page,
                finished: this.filters.finished,
                productnr: this.filters.productnr,
                ordernr: this.filters.ordernr,
                customer: this.filters.customer,
                reference: this.filters.reference,
                producttype: this.filters.producttype,
                ralnr: this.filters.ralnr,
                raltype: this.filters.raltype,
                status: this.filters.status,
                productionDate: this.filters.productionDate,
                deliveryDateId: this.filters.deliveryDateId,
                deliveryMethod: this.filters.deliveryMethod,
                sortField: this.filters.sortField,
                sortOrder: this.filters.sortOrder,
                specialFilter: this.filters.specialFilter,
            };

            payload.deliveryDate = this.filters.deliveryDate;
            payload.deliveryDateIN = this.filters.deliveryDateIN;
            payload.supplierId = this.filters.supplierId;

            this.$store.commit('productEditOverview/setFilters', this.filters, {
                root: true,
            });

            this.loading = true;

            productApi
                .editOverview(payload)
                .then((response) => {
                    if (this.page == 0) {
                        this.products = [];
                    }

                    this.products = [
                        ...this.products,
                        ...response.data.data.map((product) => {
                            return {
                                ...product,
                                productionDateSaving: false,
                                productionDateError: false,
                                priceSaving: false,
                                priceError: false,
                            };
                        }),
                    ];

                    this.$nextTick(() => {
                        this.tweakDatePickers();
                    });
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.product_edit_overview.unknown_error_fetching_overview'
                        )
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        clearProductEditOverview() {
            this.filters = { ...clearedFilters };
            this.loadWithReset();
        },
        loadWithReset() {
            this.page = 0;
            this.load();
        },
        updateProductionDateForProduct(product, $event) {
            const value = $event.target.value;
            if (value == '') {
                return;
            }

            product.productionDateError = false;

            if (!isNumber(value) || value.length != 4) {
                product.productionDateError = true;
                return;
            }

            product.productionDateSaving = true;
            productApi
                .updateProductionDate(product.productId, value)
                .then(() => {
                    product.productionDate = value;
                })
                .catch(() => {
                    product.productionDateError = true;
                    showErrorMessage(
                        this.trans(
                            'pages.product_edit_overview.unknown_error_saving_production_date'
                        )
                    );
                })
                .finally(() => {
                    product.productionDateSaving = false;
                });
        },
        updatePriceForProduct(product, $event) {
            const value = $event.target.value;
            if (value == '') {
                return;
            }

            product.priceError = false;
            if (!isNumber(value)) {
                product.priceError = true;
                return;
            }

            product.priceSaving = true;
            productApi
                .updatePrice(product.productId, value)
                .then(() => {
                    product.price = value;
                })
                .catch(() => {
                    product.priceError = true;
                    showErrorMessage(
                        this.trans(
                            'pages.product_edit_overview.unknown_error_saving_price'
                        )
                    );
                })
                .finally(() => {
                    product.priceSaving = false;
                });
        },
    },
};
</script>
